import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import Header from "../../components/navigation/Header";
import Footer from "../../components/homepage/Footer";
import { getBlogs, getFeaturedBlog, deleteBlog, toggleSaveBlog } from "../../service/ApiService/BlogApi";
import { CATEGORY_MAP, formatDate } from "./BlogConstants";
import SubmitBlogModal from "./SubmitBlogModal";
import ImageWithFallback from "../../components/common/ImageWithFallback";
import blogFallback from "../../assets/bai-viet.png";
import { useTranslation } from "react-i18next";



export default function BlogPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [blogs, setBlogs] = useState([]);
    const [trendingBlogs, setTrendingBlogs] = useState([]);
    const [featuredBlogs, setFeaturedBlogs] = useState([]);
    const [currentFeaturedIndex, setCurrentFeaturedIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [searchInput, setSearchInput] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const [viewMode, setViewMode] = useState("grid"); // "grid" | "list"
    const blogsLengthRef = useRef(0);

    // Submission Form State
    const isAuthenticated = useSelector((state) => state.user.isAuthenticated);
    const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
    const [editingBlog, setEditingBlog] = useState(null);
    const currentUser = useSelector((state) => state.user.account);
    const [activeMenuId, setActiveMenuId] = useState(null);
    const menuRef = useRef(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setActiveMenuId(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        blogsLengthRef.current = blogs.length;
    }, [blogs]);

    const fetchFeatured = useCallback(async () => {
        try {
            const res = await getFeaturedBlog();
            setFeaturedBlogs(Array.isArray(res) ? res : (res ? [res] : []));
        } catch (error) {
            console.error("Error fetching featured blog:", error);
            setFeaturedBlogs([]);
        }
    }, []);

    const fetchTrending = useCallback(async () => {
        try {
            const res = await getBlogs({ limit: 3, sort: "popular" });
            setTrendingBlogs(res.blogs || []);
        } catch (error) {
            console.error("Error fetching trending blogs:", error);
            setTrendingBlogs([]);
        }
    }, []);

    const fetchBlogs = useCallback(async () => {
        try {
            if (page === 1 && blogsLengthRef.current === 0) setLoading(true);
            else if (page > 1) setLoadingMore(true);

            const limit = page === 1 ? 4 : 2;
            const backendPage = page === 1 ? 1 : (page + 1);

            const params = { page: backendPage, limit };
            
            if (selectedCategory === "MY_POSTS") {
                params.authorName = currentUser.username || currentUser.email;
            } else if (selectedCategory === "SAVED_POSTS") {
                params.authorName = currentUser.username || currentUser.email;
                params.isSaved = true; 
            } else if (selectedCategory) {
                params.category = selectedCategory;
            }

            if (searchTerm) params.search = searchTerm;

            const res = await getBlogs(params);

            if (page === 1) {
                setBlogs(res.blogs || []);
            } else {
                setBlogs(prev => [...prev, ...(res.blogs || [])]);
            }

            setTotalCount(res.total || 0);
        } catch (err) {
            console.error("Error fetching blogs:", err);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [page, selectedCategory, searchTerm, currentUser.username, currentUser.email]);

    useEffect(() => {
        fetchFeatured();
        fetchTrending();
    }, [fetchFeatured, fetchTrending]);

    // Timer for Featured Rotation
    useEffect(() => {
        if (featuredBlogs.length <= 1) return;
        
        const interval = setInterval(() => {
            setCurrentFeaturedIndex((prev) => (prev + 1) % featuredBlogs.length);
        }, 3000);
        
        return () => clearInterval(interval);
    }, [featuredBlogs, currentFeaturedIndex]);

    useEffect(() => {
        fetchBlogs();
    }, [fetchBlogs]);

    // Debounce Search
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchTerm(searchInput);
            setPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const handleSearch = (e) => {
        if (e) e.preventDefault();
        setSearchTerm(searchInput);
        setPage(1);
    };

    const handleClearSearch = () => {
        setSearchInput("");
        setSearchTerm("");
        setIsSearchExpanded(false);
        setPage(1);
    };

    const handleCategoryFilter = (cat) => {
        setSelectedCategory(cat === selectedCategory ? null : cat);
        setPage(1);
    };

    const handleCollapse = () => {
        // Slice locally first to avoid flicker
        setBlogs(prev => prev.slice(0, 4));
        setPage(1);
        // Scroll smoothly to start of insights
        const headerOffset = 100;
        const element = document.getElementById('insights-start');
        const elementPosition = element?.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
            top: offsetPosition || 0,
            behavior: "smooth"
        });
    };

    // Use all blogs for the grid
    const gridBlogs = blogs;

    const handleOpenSubmitModal = () => {
        if (!isAuthenticated) {
            toast.error(t('blog.login_to_submit'));
            navigate("/signin");
            return;
        }
        setIsSubmitModalOpen(true);
    };

    // Category hover class (AI_TECH uses accent-cyan hover)
    const getCardHoverClass = (category) => {
        return category === "AI_TECH"
            ? "group-hover:text-accent-cyan"
            : "group-hover:text-primary";
    };

    const handleEditBlog = (e, blog) => {
        e.stopPropagation();
        setEditingBlog(blog);
        setIsSubmitModalOpen(true);
        setActiveMenuId(null);
    };

    const handleDeleteBlog = async (e, blogId) => {
        e.stopPropagation();
        if (window.confirm(t('blog.confirm_delete'))) {
            try {
                await deleteBlog(blogId);
                toast.success(t('blog.delete_success'));
                fetchBlogs();
            } catch (err) {
                toast.error(t('blog.delete_error'));
            }
        }
        setActiveMenuId(null);
    };

    const handleSaveBlog = async (e, blogId) => {
        e.stopPropagation();
        if (!isAuthenticated) {
            toast.error(t('blog.login_to_save'));
            navigate("/signin");
            return;
        }

        try {
            const userName = currentUser.username || currentUser.email;
            const res = await toggleSaveBlog(blogId, userName);
            
            // Update local state to reflect the change immediately
            if (selectedCategory === "SAVED_POSTS" && !res.isSaved) {
                setBlogs(prev => prev.filter(blog => blog._id !== blogId));
            } else {
                setBlogs(prev => prev.map(blog => {
                    if (blog._id === blogId) {
                        return res.blog;
                    }
                    return blog;
                }));
            }

            if (res.isSaved) {
                toast.success(t('blog.save_success'));
            } else {
                toast.info(t('blog.unsave_success'));
            }
        } catch (err) {
            toast.error(t('blog.save_error'));
        }
    };

    const toggleMenu = (e, blogId) => {
        e.stopPropagation();
        setActiveMenuId(activeMenuId === blogId ? null : blogId);
    };

    return (
        <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#f6f6f8", fontFamily: "'Inter', sans-serif" }} data-theme="light">
            <Header />

            {/* ============ PREMIUM HERO BANNER ============ */}
            <section className="relative w-full overflow-hidden bg-slate-900 pt-32 pb-20 lg:pt-48 lg:pb-32" data-theme="dark">
                {/* Background Image with Overlay */}
                <div className="absolute inset-0 z-0">
                    <img
                        src="/C:/Users/LENOVO/.gemini/antigravity/brain/02465285-41b5-4567-a127-34f04a430823/blog_hero_banner_background_1772367323158.png"
                        alt="Hero Decor"
                        className="h-full w-full object-cover opacity-40"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent" />
                </div>

                <div className="relative z-10 mx-auto max-w-[1440px] px-6 md:px-10">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-16">
                        {/* Hero Text Content */}
                        <div className="max-w-3xl">
                            <nav className="mb-6 flex items-center gap-2 text-sm font-medium text-slate-400">
                                <span className="hover:text-primary cursor-pointer transition-colors" onClick={() => navigate("/")}>{t('blog.breadcrumb_home')}</span>
                                <span className="material-symbols-outlined text-xs">chevron_right</span>
                                <span className="text-white">{t('blog.title')}</span>
                            </nav>

                            <h1 className="font-display text-5xl md:text-7xl font-black text-white leading-[1.1] mb-6">
                                {t('blog.hero_title_1')} <span className="text-primary italic">{t('blog.hero_title_2')}</span> <br />
                                {t('blog.hero_title_3')}
                            </h1>

                            <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-10 max-w-2xl">
                                {t('blog.hero_subtitle')}
                            </p>

                        </div>

                        {/* Featured Quick Card - Made wider with rotation logic */}
                        {featuredBlogs.length > 0 && (
                            <div className="hidden lg:flex flex-col w-[550px] shrink-0 relative">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={featuredBlogs[currentFeaturedIndex]?._id}
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        transition={{ duration: 0.5 }}
                                        onClick={() => navigate(`/blog/${featuredBlogs[currentFeaturedIndex]._id}`)}
                                        className="flex flex-col w-full rounded-3xl overflow-hidden glass-panel border-white/10 shadow-2xl cursor-pointer group hover:scale-[1.02] transition-all duration-500"
                                    >
                                        <div className="aspect-[21/9] overflow-hidden relative">
                                            {/\.(mp4|mov|avi|webm)$/i.test(featuredBlogs[currentFeaturedIndex].coverImage) ? (
                                                <video 
                                                    src={featuredBlogs[currentFeaturedIndex].coverImage} 
                                                    className="w-full h-full object-cover" 
                                                    muted 
                                                    autoPlay 
                                                    loop 
                                                />
                                            ) : (
                                                <ImageWithFallback 
                                                    src={featuredBlogs[currentFeaturedIndex].coverImage} 
                                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                                                    fallback={blogFallback}
                                                    alt="Featured" 
                                                />
                                            )}
                                            {/\.(mp4|mov|avi|webm)$/i.test(featuredBlogs[currentFeaturedIndex].coverImage) && (
                                                <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                                    <div className="h-14 w-14 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 text-white shadow-2xl">
                                                        <span className="material-symbols-outlined text-4xl fill-current">play_arrow</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-8">
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="inline-block px-3 py-1 rounded-full bg-primary/20 text-primary text-[10px] font-black uppercase tracking-widest">
                                                        {t('blog.featured_label')}
                                                    </span>
                                                    {featuredBlogs.length > 1 && (
                                                        <div className="flex gap-1.5 ml-3">
                                                            {featuredBlogs.map((_, idx) => (
                                                                <button 
                                                                    key={idx}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setCurrentFeaturedIndex(idx);
                                                                    }}
                                                                    className={`h-2 w-2 rounded-full transition-all duration-500 hover:bg-white/40 ${idx === currentFeaturedIndex ? "bg-primary w-5" : "bg-white/20"}`}
                                                                    title={`View featured blog ${idx + 1}`}
                                                                />
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                                                    {t('blog.read_time', { count: featuredBlogs[currentFeaturedIndex].readTime })}
                                                </span>
                                            </div>
                                            <h3 className="text-2xl font-bold text-white mb-3 line-clamp-2 group-hover:text-primary transition-colors leading-snug h-[4rem] flex items-center">
                                                {featuredBlogs[currentFeaturedIndex].title}
                                            </h3>
                                            <p className="text-sm text-slate-400 line-clamp-2 mb-6 leading-relaxed h-[3rem]">
                                                {featuredBlogs[currentFeaturedIndex].description}
                                            </p>
                                            <div className="flex items-center justify-between pt-6 border-t border-white/5">
                                                <div className="flex items-center gap-3">
                                                    {featuredBlogs[currentFeaturedIndex].author?.avatar ? (
                                                        <div className="h-10 w-10 rounded-full border border-white/10 p-0.5">
                                                            <img src={featuredBlogs[currentFeaturedIndex].author.avatar} alt={featuredBlogs[currentFeaturedIndex].author.name} className="h-full w-full rounded-full object-cover" />
                                                        </div>
                                                    ) : (
                                                        <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm font-bold">
                                                            {featuredBlogs[currentFeaturedIndex].author?.name?.charAt(0)}
                                                        </div>
                                                    )}
                                                    <div>
                                                        <span className="block text-sm text-white font-bold">{featuredBlogs[currentFeaturedIndex].author?.name}</span>
                                                        <span className="block text-[10px] text-slate-500 uppercase font-medium">{formatDate(featuredBlogs[currentFeaturedIndex].createdAt)}</span>
                                                    </div>
                                                </div>
                                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-white group-hover:bg-primary group-hover:text-white transition-all">
                                                    <span className="material-symbols-outlined text-sm">arrow_outward</span>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        )}
                    </div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute right-0 bottom-0 h-64 w-64 bg-primary/20 blur-[120px] rounded-full translate-x-1/2 translate-y-1/2" />
                <div className="absolute left-1/4 top-0 h-40 w-40 bg-accent-cyan/10 blur-[100px] rounded-full -translate-y-1/2" />
            </section>

            <main className="mx-auto w-full max-w-[1440px] px-6 py-10 md:px-10">
                {/* Standalone featured section removed for cleaner hero integration */}


                <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
                    {/* ============ MAIN CONTENT FEED ============ */}
                    <div className="lg:col-span-8">
                        {/* ---- Toolbar: Title + Search + View Toggles ---- */}
                        <div id="insights-start" className="flex items-center justify-between mb-8 gap-4">
                            <h2 className="font-display text-3xl font-bold tracking-tight shrink-0" style={{ color: "#0d0e1b" }}>
                                {t('blog.latest_posts')}
                            </h2>

                            <div className="flex items-center gap-2">
                                {/* Search */}
                                <form
                                    onSubmit={handleSearch}
                                    className="relative flex items-center"
                                >
                                    <div
                                        className="flex items-center rounded-xl bg-white overflow-hidden transition-all duration-300 ease-in-out"
                                        style={{
                                            border: "1px solid rgba(99,102,241,0.12)",
                                            width: isSearchExpanded ? "280px" : "42px",
                                            height: "42px",
                                        }}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (isSearchExpanded && searchInput) {
                                                    handleSearch({ preventDefault: () => { } });
                                                } else {
                                                    setIsSearchExpanded(!isSearchExpanded);
                                                }
                                            }}
                                            className="flex h-[42px] w-[42px] shrink-0 items-center justify-center text-primary hover:text-primary/80 transition-colors"
                                        >
                                            <span className="material-symbols-outlined text-xl">search</span>
                                        </button>

                                        <input
                                            value={searchInput}
                                            onChange={(e) => setSearchInput(e.target.value)}
                                            onBlur={() => {
                                                if (!searchInput) setIsSearchExpanded(false);
                                            }}
                                            className="h-full w-full border-none bg-transparent pr-3 text-sm outline-none placeholder:text-slate-400"
                                            style={{
                                                color: "#0d0e1b",
                                                opacity: isSearchExpanded ? 1 : 0,
                                                pointerEvents: isSearchExpanded ? "auto" : "none",
                                                transition: "opacity 0.2s ease",
                                            }}
                                            placeholder={t('blog.search_placeholder')}
                                            type="text"
                                            autoFocus={isSearchExpanded}
                                        />

                                        {isSearchExpanded && searchInput && (
                                            <button
                                                type="button"
                                                onClick={handleClearSearch}
                                                className="flex h-6 w-6 shrink-0 items-center justify-center mr-2 rounded-full bg-slate-100 text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition-colors"
                                            >
                                                <span className="material-symbols-outlined text-sm">close</span>
                                            </button>
                                        )}
                                    </div>
                                </form>

                                {/* Active search indicator */}
                                {searchTerm && (
                                    <div className="flex items-center gap-1.5 rounded-lg bg-primary/10 px-3 py-1.5">
                                        <span className="text-xs font-semibold text-primary truncate max-w-[100px]">
                                            "{searchTerm}"
                                        </span>
                                        <button
                                            onClick={handleClearSearch}
                                            className=" text-primary/60 hover:text-primary transition-colors"
                                        >
                                            <span className="material-symbols-outlined text-sm">close</span>
                                        </button>
                                    </div>
                                )}

                                {/* View toggles */}
                                <button
                                    onClick={() => setViewMode("grid")}
                                    className={`h-[42px] w-[42px] flex items-center justify-center rounded-lg border transition-all ${viewMode === "grid"
                                        ? "bg-primary text-white border-primary shadow-md shadow-primary/20"
                                        : "bg-white text-slate-400 hover:text-primary border-transparent"
                                        }`}
                                    style={viewMode !== "grid" ? { borderColor: "rgba(99,102,241,0.1)" } : {}}
                                >
                                    <span className="material-symbols-outlined text-xl">grid_view</span>
                                </button>
                                <button
                                    onClick={() => setViewMode("list")}
                                    className={`h-[42px] w-[42px] flex items-center justify-center rounded-lg border transition-all ${viewMode === "list"
                                        ? "bg-primary text-white border-primary shadow-md shadow-primary/20"
                                        : "bg-white text-slate-400 hover:text-primary border-transparent"
                                        }`}
                                    style={viewMode !== "list" ? { borderColor: "rgba(99,102,241,0.1)" } : {}}
                                >
                                    <span className="material-symbols-outlined text-xl">view_list</span>
                                </button>
                            </div>
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center py-20">
                                <div className="flex flex-col items-center gap-3">
                                    <div className="h-10 w-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
                                    <span className="text-sm font-medium" style={{ color: "#4c4d9a" }}>{t('blog.loading')}</span>
                                </div>
                            </div>
                        ) : gridBlogs.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 text-center">
                                <span className="material-symbols-outlined text-6xl text-slate-300 mb-4">article</span>
                                <h3 className="text-xl font-bold text-slate-400 mb-2">{t('blog.no_posts')}</h3>
                                <p className="text-sm text-slate-400">{t('blog.no_posts_subtitle')}</p>
                            </div>
                        ) : (
                            <>
                                {/* ===== GRID VIEW ===== */}
                                {viewMode === "grid" && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        {gridBlogs.map((blog) => {
                                            const catInfo = CATEGORY_MAP[blog.category] || { label: blog.category, color: "bg-primary" };
                                            return (
                                                <article
                                                    key={blog._id}
                                                    onClick={() => navigate(`/blog/${blog._id}`)}
                                                    className="group flex flex-col rounded-xl bg-white overflow-hidden shadow-sm hover:shadow-xl hover:shadow-primary/5 transition-all duration-300 cursor-pointer"
                                                    style={{ border: "1px solid rgba(99,102,241,0.05)" }}
                                                >
                                                    <div className="aspect-[16/10] overflow-hidden relative bg-slate-100 flex items-center justify-center">
                                                        {/\.(mp4|mov|avi|webm)$/i.test(blog.coverImage) ? (
                                                            <video 
                                                                src={blog.coverImage} 
                                                                className="w-full h-full object-cover" 
                                                                muted 
                                                            />
                                                        ) : (
                                                            <ImageWithFallback
                                                                src={blog.coverImage}
                                                                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110 h-full w-full object-cover"
                                                                fallback={blogFallback}
                                                                alt={blog.title}
                                                            />
                                                        )}
                                                        {/\.(mp4|mov|avi|webm)$/i.test(blog.coverImage) && (
                                                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none group-hover:bg-black/10 transition-colors">
                                                                <div className="h-10 w-10 rounded-full bg-white/80 flex items-center justify-center text-primary shadow-lg border border-white/50">
                                                                    <span className="material-symbols-outlined text-2xl fill-current">play_arrow</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div className="absolute top-4 left-4 flex gap-2">
                                                            <span className={`rounded-lg ${catInfo.color} px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white`}>
                                                                {t(`blog.categories.${blog.category}`, { defaultValue: catInfo.label })}
                                                            </span>
                                                        </div>

                                                        {/* Actions Menu */}
                                                        <div className="absolute top-4 right-4 z-20">
                                                            {blog.author?.name === (currentUser.username || currentUser.email) ? (
                                                                <div className="relative" ref={activeMenuId === blog._id ? menuRef : null}>
                                                                    <button
                                                                        onClick={(e) => toggleMenu(e, blog._id)}
                                                                        className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900/40 backdrop-blur-md text-white hover:bg-slate-900/60 shadow-lg transition-all border border-white/10"
                                                                    >
                                                                        <span className="material-symbols-outlined text-xl">more_horiz</span>
                                                                    </button>
                                                                    
                                                                    <AnimatePresence>
                                                                        {activeMenuId === blog._id && (
                                                                            <motion.div
                                                                                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                                                                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                                                                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                                                                                className="absolute right-0 mt-2 w-32 rounded-xl bg-white shadow-xl border border-slate-100 py-1 overflow-hidden"
                                                                            >
                                                                                <button
                                                                                    onClick={(e) => handleEditBlog(e, blog)}
                                                                                    className="flex w-full items-center gap-2 px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50 hover:text-primary transition-colors"
                                                                                >
                                                                                    <span className="material-symbols-outlined text-sm">edit</span>
                                                                                    Edit
                                                                                </button>
                                                                                <button
                                                                                    onClick={(e) => handleDeleteBlog(e, blog._id)}
                                                                                    className="flex w-full items-center gap-2 px-4 py-2.5 text-xs font-bold text-red-500 hover:bg-red-50/50 transition-colors"
                                                                                >
                                                                                    <span className="material-symbols-outlined text-sm">delete</span>
                                                                                    Delete
                                                                                </button>
                                                                            </motion.div>
                                                                        )}
                                                                    </AnimatePresence>
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    onClick={(e) => handleSaveBlog(e, blog._id)}
                                                                    className={`flex h-8 w-8 items-center justify-center rounded-full backdrop-blur-md transition-all shadow-lg border border-white/10 ${
                                                                        blog.savedBy?.includes(currentUser.username || currentUser.email)
                                                                            ? "bg-amber-500 text-white"
                                                                            : "bg-slate-900/40 text-white hover:bg-slate-900/60"
                                                                    }`}
                                                                >
                                                                    <span className={`material-symbols-outlined text-xl ${
                                                                        blog.savedBy?.includes(currentUser.username || currentUser.email) ? "fill-current" : ""
                                                                    }`}>bookmark</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-1 flex-col p-6">
                                                        <h3
                                                            className={`font-display text-xl font-bold leading-snug ${getCardHoverClass(blog.category)} transition-colors mb-3`}
                                                            style={{ color: "#0d0e1b" }}
                                                        >
                                                            {blog.title}
                                                        </h3>
                                                        <p className="text-sm line-clamp-3 mb-6 flex-1 leading-relaxed" style={{ color: "#4c4d9a" }}>
                                                            {blog.description}
                                                        </p>
                                                        <div className="flex items-center justify-between pt-4" style={{ borderTop: "1px solid rgba(99,102,241,0.05)" }}>
                                                            <div className="flex items-center gap-3">
                                                                {blog.author?.avatar ? (
                                                                    <div
                                                                        className="h-8 w-8 rounded-full bg-primary/10 bg-cover bg-center"
                                                                        style={{ backgroundImage: `url('${blog.author.avatar}')` }}
                                                                    />
                                                                ) : (
                                                                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                                                        <span className="material-symbols-outlined text-primary text-sm">person</span>
                                                                    </div>
                                                                )}
                                                                <span className="text-xs font-semibold" style={{ color: "#0d0e1b" }}>
                                                                    {blog.author?.name}
                                                                </span>
                                                            </div>
                                                            <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "rgba(76,77,154,0.6)" }}>
                                                                {t('blog.read_time', { count: blog.readTime })}
                                                            </span>
                                                            <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest" style={{ color: "rgba(76,77,154,0.6)" }}>
                                                                <span className="material-symbols-outlined text-xs">visibility</span>
                                                                {blog.views || 0}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </article>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* ===== LIST VIEW ===== */}
                                {viewMode === "list" && (
                                    <div className="flex flex-col gap-5">
                                        {gridBlogs.map((blog, index) => {
                                            const catInfo = CATEGORY_MAP[blog.category] || { label: blog.category, color: "bg-primary" };
                                            return (
                                                <article
                                                    key={blog._id}
                                                    onClick={() => navigate(`/blog/${blog._id}`)}
                                                    className="group flex flex-row rounded-xl bg-white overflow-hidden shadow-sm hover:shadow-xl hover:shadow-primary/5 transition-all duration-300 cursor-pointer"
                                                    style={{ border: "1px solid rgba(99,102,241,0.05)" }}
                                                >
                                                    {/* Thumbnail */}
                                                    <div className="relative w-[200px] md:w-[260px] shrink-0 overflow-hidden bg-slate-100 flex items-center justify-center">
                                                        {/\.(mp4|mov|avi|webm)$/i.test(blog.coverImage) ? (
                                                            <video 
                                                                src={blog.coverImage} 
                                                                className="w-full h-full object-cover" 
                                                                muted 
                                                            />
                                                        ) : (
                                                            <div
                                                                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                                                                style={{ backgroundImage: `url('${blog.coverImage}')` }}
                                                            />
                                                        )}
                                                        {/\.(mp4|mov|avi|webm)$/i.test(blog.coverImage) && (
                                                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none group-hover:bg-black/10 transition-colors">
                                                                <div className="h-10 w-10 rounded-full bg-white/80 flex items-center justify-center text-primary shadow-lg border border-white/50">
                                                                    <span className="material-symbols-outlined text-2xl fill-current">play_arrow</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div className="absolute top-3 left-3">
                                                            <span className={`rounded-lg ${catInfo.color} px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white`}>
                                                                {t(`blog.categories.${blog.category}`, { defaultValue: catInfo.label })}
                                                            </span>
                                                        </div>

                                                        {/* Quick Action for List View */}
                                                        <div className="absolute top-3 right-3 z-20">
                                                            {blog.author?.name === (currentUser.username || currentUser.email) ? (
                                                                <div className="relative" ref={activeMenuId === blog._id ? menuRef : null}>
                                                                    <button
                                                                        onClick={(e) => toggleMenu(e, blog._id)}
                                                                        className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-900/40 backdrop-blur-md text-white hover:bg-slate-900/60 shadow-lg transition-all border border-white/10"
                                                                    >
                                                                        <span className="material-symbols-outlined text-lg">more_horiz</span>
                                                                    </button>
                                                                    
                                                                    <AnimatePresence>
                                                                        {activeMenuId === blog._id && (
                                                                            <motion.div
                                                                                initial={{ opacity: 0, scale: 0.95, x: 10 }}
                                                                                animate={{ opacity: 1, scale: 1, x: 0 }}
                                                                                exit={{ opacity: 0, scale: 0.95, x: 10 }}
                                                                                className="absolute left-full ml-2 top-0 w-32 rounded-xl bg-white shadow-xl border border-slate-100 py-1 overflow-hidden"
                                                                            >
                                                                                <button
                                                                                    onClick={(e) => handleEditBlog(e, blog)}
                                                                                    className="flex w-full items-center gap-2 px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50 hover:text-primary transition-colors"
                                                                                >
                                                                                    <span className="material-symbols-outlined text-sm">edit</span>
                                                                                    Edit
                                                                                </button>
                                                                                <button
                                                                                    onClick={(e) => handleDeleteBlog(e, blog._id)}
                                                                                    className="flex w-full items-center gap-2 px-4 py-2.5 text-xs font-bold text-red-500 hover:bg-red-50/50 transition-colors"
                                                                                >
                                                                                    <span className="material-symbols-outlined text-sm">delete</span>
                                                                                    Delete
                                                                                </button>
                                                                            </motion.div>
                                                                        )}
                                                                    </AnimatePresence>
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    onClick={(e) => handleSaveBlog(e, blog._id)}
                                                                    className={`flex h-7 w-7 items-center justify-center rounded-full backdrop-blur-md transition-all shadow-lg border border-white/10 ${
                                                                        blog.savedBy?.includes(currentUser.username || currentUser.email)
                                                                            ? "bg-amber-500 text-white"
                                                                            : "bg-slate-900/40 text-white hover:bg-slate-900/60"
                                                                    }`}
                                                                >
                                                                    <span className={`material-symbols-outlined text-lg ${
                                                                        blog.savedBy?.includes(currentUser.username || currentUser.email) ? "fill-current" : ""
                                                                    }`}>bookmark</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Content */}
                                                    <div className="flex flex-1 flex-col justify-center p-5 md:p-6 min-w-0">
                                                        <div className="flex items-center gap-3 mb-2">
                                                            <span className="text-xs font-semibold text-primary">
                                                                #{index + 1 + (page - 1) * 8}
                                                            </span>
                                                            <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "rgba(76,77,154,0.6)" }}>
                                                                {formatDate(blog.createdAt)}
                                                            </span>
                                                        </div>
                                                        <h3
                                                            className={`font-display text-lg font-bold leading-snug ${getCardHoverClass(blog.category)} transition-colors mb-2 line-clamp-2`}
                                                            style={{ color: "#0d0e1b" }}
                                                        >
                                                            {blog.title}
                                                        </h3>
                                                        <p className="text-sm line-clamp-2 mb-4 leading-relaxed hidden md:block" style={{ color: "#4c4d9a" }}>
                                                            {blog.description}
                                                        </p>
                                                        <div className="flex items-center justify-between mt-auto">
                                                            <div className="flex items-center gap-2.5">
                                                                {blog.author?.avatar ? (
                                                                    <div
                                                                        className="h-7 w-7 rounded-full bg-primary/10 bg-cover bg-center"
                                                                        style={{ backgroundImage: `url('${blog.author.avatar}')` }}
                                                                    />
                                                                ) : (
                                                                    <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center">
                                                                        <span className="material-symbols-outlined text-primary text-xs">person</span>
                                                                    </div>
                                                                )}
                                                                <span className="text-xs font-semibold" style={{ color: "#0d0e1b" }}>
                                                                    {blog.author?.name}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-4">
                                                                <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "rgba(76,77,154,0.6)" }}>
                                                                    {t('blog.read_time', { count: blog.readTime })}
                                                                </span>
                                                                <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest" style={{ color: "rgba(76,77,154,0.6)" }}>
                                                                    <span className="material-symbols-outlined text-xs">visibility</span>
                                                                    {blog.views || 0}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </article>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Load More / Show Less Button */}
                                {gridBlogs.length > 0 && totalCount > 4 && (
                                    <div className="mt-12 flex justify-center">
                                        {gridBlogs.length < totalCount ? (
                                            <button
                                                onClick={() => setPage(prev => prev + 1)}
                                                disabled={loadingMore}
                                                className="flex items-center gap-2 rounded-xl border-2 px-8 py-3 font-bold text-primary hover:bg-primary hover:text-white transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
                                                style={{ borderColor: "rgba(99,102,241,0.1)" }}
                                            >
                                                {loadingMore ? (
                                                    <>
                                                        <div className="h-4 w-4 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
                                                        <span>{t('blog.loading')}</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span>{t('blog.view_more')}</span>
                                                        <span className="material-symbols-outlined group-hover:translate-y-1 transition-transform">expand_more</span>
                                                    </>
                                                )}
                                            </button>
                                        ) : (
                                            <button
                                                onClick={handleCollapse}
                                                className="flex items-center gap-2 rounded-xl border-2 px-8 py-3 font-bold text-primary hover:bg-white hover:text-primary transition-all border-primary shadow-lg shadow-primary/10"
                                            >
                                                <span>{t('blog.collapse')}</span>
                                                <span className="material-symbols-outlined">expand_less</span>
                                            </button>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* ============ SIDEBAR ============ */}
                    <aside className="lg:col-span-4 space-y-12">
                        {/* Popular Categories */}
                        <div className="rounded-xl bg-white p-8 shadow-sm" style={{ border: "1px solid rgba(99,102,241,0.05)" }}>
                            <h3 className="font-display text-xl font-bold mb-6 flex items-center gap-2" style={{ color: "#0d0e1b" }}>
                                <span className="material-symbols-outlined text-primary">category</span>
                                {t('blog.popular_categories')}
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(CATEGORY_MAP).map(([key, { label }]) => {
                                    const isAiTech = key === "AI_TECH";
                                    return (
                                        <button
                                            key={key}
                                            onClick={() => handleCategoryFilter(key)}
                                            className={`rounded-lg px-4 py-2 text-sm font-medium transition-all ${selectedCategory === key
                                                ? "bg-primary text-white"
                                                : isAiTech
                                                    ? "bg-accent-cyan/10 text-accent-cyan font-bold hover:bg-accent-cyan hover:text-white"
                                                    : "bg-primary/5 hover:bg-primary hover:text-white"
                                                }`}
                                            style={
                                                selectedCategory !== key && !isAiTech
                                                    ? { color: "#0d0e1b" }
                                                    : {}
                                            }
                                        >
                                            {t(`blog.categories.${key}`, { defaultValue: label })}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Separator */}
                            <div className="my-6 border-t border-slate-100" />

                            {/* Personal Filters */}
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => handleCategoryFilter("MY_POSTS")}
                                    className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-bold transition-all ${selectedCategory === "MY_POSTS"
                                        ? "bg-slate-900 text-white"
                                        : "bg-slate-100 text-slate-600 hover:bg-slate-900 hover:text-white"
                                        }`}
                                >
                                    <span className="material-symbols-outlined text-sm">person</span>
                                    {t('blog.my_posts')}
                                </button>
                                <button
                                    onClick={() => handleCategoryFilter("SAVED_POSTS")}
                                    className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-bold transition-all ${selectedCategory === "SAVED_POSTS"
                                        ? "bg-amber-500 text-white"
                                        : "bg-slate-100 text-slate-600 hover:bg-amber-500 hover:text-white"
                                        }`}
                                >
                                    <span className="material-symbols-outlined text-sm">bookmark</span>
                                    {t('blog.saved_posts')}
                                </button>
                            </div>
                        </div>

                        {/* Trending Now */}
                        {trendingBlogs.length > 0 && (
                            <div className="rounded-xl bg-white p-8 shadow-sm" style={{ border: "1px solid rgba(99,102,241,0.05)" }}>
                                <h3 className="font-display text-xl font-bold mb-6 flex items-center gap-2" style={{ color: "#0d0e1b" }}>
                                    <span className="material-symbols-outlined text-primary">trending_up</span>
                                    {t('blog.trending')}
                                </h3>
                                <div className="space-y-6">
                                    {trendingBlogs.map((blog) => {
                                        const catInfo = CATEGORY_MAP[blog.category] || { label: blog.category };
                                        const isTech = blog.category === "AI_TECH";
                                        return (
                                            <div
                                                key={blog._id}
                                                onClick={() => navigate(`/blog/${blog._id}`)}
                                                className="group flex gap-4 cursor-pointer"
                                            >
                                                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg">
                                                    <div
                                                        className="h-full w-full bg-cover bg-center group-hover:scale-110 transition-transform duration-300"
                                                        style={{ backgroundImage: `url('${blog.coverImage}')` }}
                                                    />
                                                </div>
                                                <div className="flex flex-col">
                                                    <h4 className="text-sm font-bold group-hover:text-primary transition-colors line-clamp-2 leading-snug" style={{ color: "#0d0e1b" }}>
                                                        {blog.title}
                                                    </h4>
                                                        <div className="flex items-center gap-2">
                                                            <span
                                                                className="text-[10px] mt-1 uppercase font-bold tracking-tighter"
                                                                style={{ color: isTech ? "#22D3EE" : "#4c4d9a" }}
                                                            >
                                                                {t(`blog.categories.${blog.category}`, { defaultValue: catInfo.label })}
                                                            </span>
                                                            <div className="flex items-center gap-0.5 text-[9px] mt-1 font-bold opacity-60">
                                                                <span className="material-symbols-outlined text-[10px]">visibility</span>
                                                                {blog.views || 0}
                                                            </div>
                                                        </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Contribute */}
                        <div className="rounded-xl bg-white p-8 shadow-sm" style={{ border: "1px solid rgba(99,102,241,0.05)" }}>
                            <h3 className="font-display text-xl font-bold mb-4 flex items-center gap-2" style={{ color: "#0d0e1b" }}>
                                <span className="material-symbols-outlined text-primary">groups</span>
                                {t('blog.collaborate')}
                            </h3>
                            <p className="text-sm mb-6 leading-relaxed" style={{ color: "#4c4d9a" }}>
                                {t('blog.collaborate_subtitle')}
                            </p>
                            <button
                                onClick={handleOpenSubmitModal}
                                className="w-full flex items-center justify-center gap-2 rounded-lg border-2 border-primary text-primary py-3 text-sm font-bold transition-all hover:bg-primary hover:text-white"
                            >
                                <span className="material-symbols-outlined text-lg">upload_file</span>
                                {t('blog.submit_post')}
                            </button>
                        </div>

                        {/* Pro Community – dark promo */}
                        <div className="relative overflow-hidden rounded-xl bg-slate-900 p-8 text-white">
                            <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary/20 blur-3xl" />
                            <div className="relative z-10">
                                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">Pro Community</span>
                                <h3 className="mt-2 text-2xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                                    {t('blog.join_community')}
                                </h3>
                                <p className="mt-4 text-sm text-slate-400 leading-relaxed">
                                    {t('blog.join_community_subtitle')}
                                </p>
                                <button className="mt-8 w-full rounded-lg bg-primary py-3 text-sm font-bold transition-all hover:bg-primary/80">
                                    {t('blog.get_early_access')}
                                </button>
                            </div>
                        </div>
                    </aside>
                </div>
            </main>

            {/* ============ NEWSLETTER SECTION ============ */}
            <section className="bg-slate-950 text-white py-20">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <span className="inline-block bg-primary/20 text-primary text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-[0.2em] mb-6 border border-primary/30">
                        {t('blog.creator_knowledge')}
                    </span>

                    <h2 className="text-4xl md:text-5xl font-bold mb-6 font-display">
                        {t('blog.newsletter_headline')}
                    </h2>

                    <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto">
                        {t('blog.newsletter_subheadline')}
                    </p>

                    <form className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto" onSubmit={(e) => e.preventDefault()}>
                        <input
                            className="flex-1 bg-slate-900 border-slate-800 rounded-xl px-6 py-4 focus:ring-primary focus:border-primary placeholder:text-slate-600 text-white"
                            placeholder={t('blog.email_placeholder')}
                            type="email"
                        />
                        <button
                            className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-xl font-bold transition-all shadow-lg shadow-primary/20"
                            type="submit"
                        >
                            {t('blog.subscribe_now')}
                        </button>
                    </form>

                    <p className="mt-6 text-slate-600 text-xs">
                        {t('blog.newsletter_footer')}
                    </p>
                </div>
            </section>

            <Footer />




            <SubmitBlogModal
                isOpen={isSubmitModalOpen}
                onClose={() => {
                    setIsSubmitModalOpen(false);
                    setEditingBlog(null);
                }}
                onSuccess={fetchBlogs}
                initialData={editingBlog}
            />
        </div>
    );
}
